import * as THREE from 'three';
import { Color, FogExp2, Group, Raycaster, Vector2, Vector3 } from 'three';
import { Lobby, makeRoomCode } from './game/lobby';
import { Session } from './game/session';
import { Viewmodel } from './game/viewmodel';
import { lootBody } from './world/loot-props';
import { ITEMS } from './sim/items';
import { GameNet } from './net/game-net';
import { SteamTransport, steamBridge, type SteamStatus } from './net/steam';
import type { Transport } from './net/transport';
import { WebSocketTransport } from './net/ws';
import { ATMOS, LIGHTS } from './render/palette';
import { Viewport } from './render/renderer';
import { installSky, moonUVFromDirection } from './render/sky';
import {
  MALL_ATMOSPHERE,
  MALL_CAMERAS,
  buildMallScene,
  mallBake,
  mallBakeEncoded,
  mallBakeReady,
  mallLights,
  mallStaticLights,
} from './scenes/mall-scene';
import { seedFromName } from './sim/rng';
import { installViewer } from './viewer';
import { zoneAt } from './world/plan';

/**
 * Bootstrap. Builds the real map and parks a camera in it. Movement, netcode and
 * the round loop hang off this once they land; until then the debug surface below
 * is how the screenshot harness and I both look at the game.
 */

const app = document.getElementById('app')!;
const viewport = new Viewport(app);
viewport.setAtmosphere(MALL_ATMOSPHERE.color, MALL_ATMOSPHERE.density);
installSky(viewport.scene, viewport.renderer, {
  moonUV: moonUVFromDirection(LIGHTS.moon.position),
  moonRadiusPx: 30,
});

const mall = buildMallScene(viewport.scene);
const lights = mallLights();

/**
 * Your hands, drawn over the top of the building in their own pass.
 *
 * Built here rather than inside the session because it outlives one: a restart
 * throws the round away and mints a new one, and rebuilding two gloves and
 * thirty-seven cached objects to do it would be a visible hitch at exactly the
 * moment the player is watching for the next night to start.
 */
const viewmodel = new Viewmodel();
viewport.attachOverlay(viewmodel.scene, viewmodel.camera);
viewmodel.useEnvironment(viewport.scene.environment);

/**
 * The lightmapper runs across every core the machine has and takes a couple of
 * seconds. Until it lands the mall is standing in moonlight with its shop
 * fittings switched off, which is a real picture rather than a black screen, so
 * this is a caption over a live building rather than a splash. It removes itself
 * on the first frame after the lights come up.
 */
const loading = document.createElement('div');
loading.id = 'baking';
loading.textContent = 'POWERING UP';
document.body.appendChild(loading);
let ready = false;
mallBakeReady().then((report) => {
  ready = true;
  loading.remove();
  console.info('[mall] lit.', JSON.stringify(report));
  // The bake replaced every lit geometry in the building, so whatever the fixed
  // shots decided about the light pool is stale.
  lights.snap(viewport.camera);
  // Insurance, and one frame's worth. The shadow map is drawn once and kept, so
  // the one thing that must not happen is for it to have been taken before the
  // building finished arriving.
  viewport.restampShadows();
});

type CameraName = keyof typeof MALL_CAMERAS;

/**
 * Fog colour per zone kind. Stepping into the service corridor should feel like
 * the air changed, not just the wallpaper — warm sodium haze back of house, cold
 * slate out front.
 */
const FOG_COLOR = {
  atrium: ATMOS.fogInterior,
  corridor: ATMOS.fogInterior,
  service: ATMOS.fogService,
  exterior: ATMOS.fogExterior,
  parking: ATMOS.fogExterior,
} as const;

/**
 * How long the air takes to change, in seconds to within 1/e.
 *
 * Zones meet along a plane, so walking through a doorway crosses the boundary in
 * one frame and a straight assignment restages the entire image between two
 * frames — the fog colour, the density and the sky behind the glazing all jump at
 * once. It does not read as walking into a different room; it reads as the
 * renderer glitching, and it happens on the exact stride where the player is
 * looking at the doorway. Easing costs one lerp a frame and the boundary stops
 * being an event.
 */
const FOG_TAU = 0.18;
const fogTarget = new Color();

/** Ease the air toward the zone the camera is in. Omit `dt` to snap, for teleports. */
function applyZoneFog(dt?: number) {
  const p = viewport.camera.position;
  const zone = zoneAt(mall.zones, p.x, p.y, p.z);
  if (!zone) return;
  fogTarget.copy(FOG_COLOR[zone.fog]);
  const density = ATMOS.density[zone.fog];
  if (dt === undefined) {
    viewport.setAtmosphere(fogTarget, density);
    return;
  }
  const fog = viewport.scene.fog as FogExp2;
  // Exponential approach, framerate-independent: the same journey through the
  // same doorway looks the same at 30fps and at 144.
  const k = 1 - Math.exp(-dt / FOG_TAU);
  viewport.setAtmosphere(
    fogTarget.copy(fog.color).lerp(FOG_COLOR[zone.fog], k),
    fog.density + (density - fog.density) * k,
  );
}

let session: Session | null = null;
let mode: 'play' | 'review' = 'review';

function setCamera(name: CameraName) {
  // A fixed shot and a first-person camera cannot both be right, so asking for
  // one is how you say you are done playing. The harness never has to know.
  setMode('review');
  const c: { pos: readonly number[]; look: readonly number[] } = MALL_CAMERAS[name];
  viewport.camera.position.set(c.pos[0], c.pos[1], c.pos[2]);
  viewport.camera.lookAt(new Vector3(c.look[0], c.look[1], c.look[2]));
  applyZoneFog();
  // A fixed shot is a teleport, so the shop lights re-pick at full brightness
  // rather than fading up — otherwise the harness photographs half-lit rooms.
  lights.snap(viewport.camera);
}
setCamera('approach');

/**
 * The review overlay: authored viewpoints on hotkeys plus a free-fly camera.
 * Installed here rather than inside the viewport because it drives `setCamera`,
 * which is main's — the fixed shots have to re-apply zone fog when they land.
 */
const viewer = installViewer({
  camera: viewport.camera,
  surface: viewport.renderer.domElement,
  shots: Object.keys(MALL_CAMERAS),
  goTo: (name) => setCamera(name as CameraName),
});

/**
 * The game itself.
 *
 * Two things want this camera: the review tool, which is how the art gets
 * checked and how the screenshot harness works, and the player. Exactly one of
 * them owns it at a time, and the handover is here rather than inside either —
 * neither of them should have to know the other exists.
 *
 * Review mode is the default until the session finishes loading Rapier, so a
 * slow WASM fetch shows the approach shot rather than a frozen black screen.
 * After that the player has it, and any call to `setCamera` — which is what the
 * harness does — takes it back.
 */
const stage = new Group();
stage.name = 'loose';
viewport.scene.add(stage);

function setMode(next: 'play' | 'review') {
  // Also the guard that lets `setCamera('approach')` run during module init:
  // the boot call asks for the mode it is already in, so it returns before it
  // can touch `viewer`, which has not been constructed yet.
  if (next === mode || (!session && next === 'play')) return;
  mode = next;
  const playing = mode === 'play';
  viewer.setEnabled(!playing);
  session?.hud.setVisible(playing);
  viewmodel.setActive(playing);
  if (playing) session!.resume();
  else session?.suspend();
}

/**
 * ONLINE.
 *
 * THE ROOM IS IN THE URL, AND JOINING IS A PAGE LOAD. That looks lazy and is
 * not. A client cannot build its `Session` until the host has told it which
 * player it is, and half of a session is keyed on that id at construction —
 * so switching an already-running solo game into a client is a teardown of
 * physics, HUD, crew, guards and audio, executed in the right order, for a
 * saving of the two seconds the lightmapper takes. Reloading gets a guaranteed
 * clean world for free, makes the shareable link and the typed code the exact
 * same code path, and is what a player expects "joining a game" to look like.
 *
 * The bare URL still boots straight into a playable solo round, untouched, so
 * every tool in `tools/` keeps working: the lobby is an overlay on top of a
 * mall that is already running, not a gate in front of one.
 */
const params = new URLSearchParams(location.search);
const ROOM = cleanCode(params.get('room'));
const HOSTING = params.get('host') === '1';

/**
 * `?capture` — the world does not advance on its own.
 *
 * The screenshot harness freezes the clock before it shoots, which makes the
 * *grain* identical but not the *world*: between page load and the first freeze
 * the mall has been simulating in real time, and how much of it has happened
 * depends on how long the lightmapper took on that particular run. Guards end up
 * a couple of metres apart between two runs of the same commit, and a torch two
 * metres to the left relights a whole shop. That is the same failure as a
 * texture seeded off `Math.random` — a diff between two runs stops meaning a
 * diff in the code — and it is the reason this flag exists.
 *
 * Under it the loop never starts. The harness calls `advance(seconds)` to step a
 * whole number of fixed 1/60 ticks, so the captured world is a pure function of
 * that number.
 */
const CAPTURE = params.has('capture');

let net: GameNet | null = null;

/**
 * The Steam bridge, if this is the desktop build with Steam actually running.
 *
 * Null in a browser, and null in the desktop build when Steam is closed — both of
 * which fall through to the relay. Nothing below may assume it exists.
 */
const steam = steamBridge();

const lobby = new Lobby(document.body, {
  onSolo: () => {
    lobby.hide();
    // The clock has been running behind the overlay since the session came up.
    // Nobody should start their night with seven minutes forty on it.
    session?.restart();
    setMode('play');
  },
  // On Steam there is no room code to mint: hosting is creating a lobby, and the
  // main process reloads us into it. The relay path keeps its four letters.
  onHost: () => {
    if (steam) {
      lobby.status('OPENING A LOBBY…');
      steam.host().catch((e: Error) => lobby.status(String(e.message).toUpperCase(), true));
    } else enter(makeRoomCode(), true);
  },
  onJoin: (code) => enter(code, false),
  onTeam: (team) => net?.setLobby({ team }),
  onReady: (ready) => net?.setLobby({ ready }),
  onStart: () => net?.start(),
  onLeave: () => {
    if (steam) steam.leave().catch(() => {});
    else enter('', false);
  },
  // Only ever wired in the desktop build; the lobby hides the button otherwise.
  onInvite: () => steam?.invite().catch(() => {}),
});
if (ROOM) lobby.prefill(ROOM);
lobby.show();

function cleanCode(s: string | null): string {
  return (s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
}

/** Reload into (or out of) a room. See the note above on why this is a reload. */
function enter(code: string, hosting: boolean): void {
  const q = code ? `?room=${code}${hosting ? '&host=1' : ''}` : '';
  const relay = params.get('relay');
  location.href = `${location.pathname}${q}${q && relay ? `&relay=${encodeURIComponent(relay)}` : ''}`;
}

/**
 * Where the lamp-post is.
 *
 * A page served from localhost talks to the node relay on :5190; anything else
 * talks to the deployed Durable Object. `?relay=` overrides both, which is how
 * a production build gets pointed at a local relay for a two-browser QA run.
 */
function relayUrl(code: string): string {
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
  const base =
    params.get('relay') ??
    (local ? 'ws://localhost:5190' : 'wss://meridian-relay.ethan-37d.workers.dev');
  return `${base.replace(/\/+$/, '')}/room/${code}`;
}

/** A pinned night, from either the four-letter name or the raw integer. */
function parseSeed(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const named = seedFromName(raw);
  if (named !== null) return named;
  const n = Number(raw);
  return Number.isFinite(n) ? n >>> 0 : undefined;
}

function openSession(opts: { role: 'solo' | 'host' | 'client'; id?: string; slot?: number }) {
  return Session.create({
    mall,
    camera: viewport.camera,
    surface: viewport.renderer.domElement,
    stage,
    viewmodel,
    hudParent: document.body,
    lights,
    staticLights: mallStaticLights(),
    role: opts.role,
    selfId: opts.id,
    selfSlot: opts.slot,
    selfName: lobby.name(),
    // `?seed=KTJM` or `?seed=1234`. Four letters is what the briefing card shows
    // and therefore what somebody would actually type back in.
    seed: parseSeed(params.get('seed')),
  }).then((s) => {
    session = s;
    setMode('play');
    console.info(`[game] session live (${opts.role}).`, JSON.stringify(s.snapshot));
    return s;
  });
}

/**
 * Everything that happens once a transport knows who it is.
 *
 * Shared verbatim between the relay and Steam, which is the whole payoff of the
 * transport interface: joining a lobby on Steam and joining a room on the web are
 * not two features that behave similarly, they are one feature holding a different
 * pipe. `label` is only what the room card shows — four letters on the web, the
 * host's persona name on Steam.
 */
function joinRoom(transport: Transport, label: string, self: string, isHost: boolean): GameNet {
  net = new GameNet(transport, {
    name: lobby.name(),
    onRoster: (roster) => lobby.setRoster(roster, isHost),
    onStart: () => {
      lobby.hide();
      // Only the host owns the world, so only the host resets it. Clients
      // are told by the `reset` event that rides along with it.
      if (isHost) session?.restart();
      setMode('play');
    },
    onError: (m) => lobby.status(m.toUpperCase(), true),
    onWelcome: (w) =>
      openSession({ role: 'client', id: w.you, slot: w.slot }).then((s) => net!.attach(s)),
  });
  lobby.enterRoom(label, self);
  // `onRoster` only fires on a change, and the host's own entry is minted in
  // the constructor — without this the host stares at an empty room it is
  // standing in until somebody else arrives.
  lobby.setRoster(net.roster, isHost);
  if (isHost) openSession({ role: 'host', id: self, slot: 0 }).then((s) => net!.attach(s));
  return net;
}

/**
 * Pick a pipe and boot.
 *
 * The order is deliberate. Steam wins when it is there, because a desktop player
 * with Steam running should never be asked to read a room code down the phone. The
 * relay is next, because it is the only thing a browser can do. Solo is the default
 * and needs neither.
 */
async function boot(): Promise<void> {
  if (steam) {
    let status: SteamStatus = { available: false };
    try {
      status = await steam.status();
    } catch {
      /* the bridge is there but the main process is unhappy; fall through */
    }
    if (status.available) {
      // A persona name is a better default than "Crew 3", and the player can still
      // overwrite it before hosting.
      lobby.steamMode(status.name ?? '');
      if (status.lobbyId) {
        const transport = new SteamTransport(steam, status);
        const label = status.isHost ? 'YOUR CREW' : 'A FRIEND’S CREW';
        joinRoom(transport, label, transport.self, transport.isHost);
        // Deferred until the handlers above exist, so anyone already in the lobby
        // is not silently missed. Same ordering rule as the relay's `onReady`.
        transport.announceExisting();
        return;
      }
      openSession({ role: 'solo' });
      return;
    }
    console.info(`[steam] ${status.reason ?? 'unavailable'} — using the relay.`);
  }

  if (!ROOM) {
    openSession({ role: 'solo' });
    return;
  }

  lobby.status(HOSTING ? 'OPENING THE ROOM…' : 'KNOCKING…');
  const transport = new WebSocketTransport({
    url: relayUrl(ROOM),
    createRoom: HOSTING,
    // Built in here rather than after the constructor because `GameNet` reads
    // `isHost` and `open` the moment it exists, and neither is known until the
    // relay has handed out a slot.
    onReady: (self, isHost) => joinRoom(transport, ROOM, self, isHost),
  });
  transport.onError = (m) => {
    lobby.show();
    lobby.leaveRoom();
    lobby.status(m.toUpperCase(), true);
  };
}
void boot();

// Backquote is the only key the game itself does not use, and dropping to the
// review camera mid-round has to be instant — half the art bugs in this map
// were found by stopping in the middle of playing and flying up to look.
window.addEventListener('keydown', (e) => {
  if (e.code !== 'Backquote') return;
  if ((e.target as HTMLElement | null)?.tagName === 'INPUT') return;
  lobby.hide();
  setMode(mode === 'play' ? 'review' : 'play');
});

let running = !CAPTURE;
let elapsed = 0;
let last = performance.now();
let frames = 0;
let fpsAccum = 0;
let fps = 0;

/** One tick of everything except drawing. `dt` is already clamped. */
function step(dt: number): void {
  elapsed += dt;
  // Not while the lightmapper is still running.
  //
  // The bake is a worker pool, so the render loop keeps turning through it and
  // the mall behind the POWERING UP caption is genuinely live — which also meant
  // the round clock was live. Fifty seconds of bake came out of a four hundred
  // and eighty second round, the fifteen second briefing expired before the
  // player ever saw it, and they arrived to a heist already in progress with no
  // tools picked. Guards walked their beats into it too, so where the shift was
  // standing when you spawned depended on how fast your laptop compiled shaders.
  //
  // Holding the sim here rather than deferring `round.start()` is deliberate:
  // it stops the whole world, not just the countdown, so a slow machine and a
  // fast one both begin the night with the building in the same state.
  if (mode === 'play') {
    if (ready) session!.update(dt);
  } else viewer.update(dt);
  // After the session, never inside it: a snapshot has to describe the world as
  // of the end of the frame. Unconditional, because a host sitting in the lobby
  // in review mode still has to answer pings and admit newcomers.
  net?.update(dt);
  // Every frame, not just on a camera jump: flying out of the service corridor
  // into the atrium has to change the air, or the zones may as well not exist.
  applyZoneFog(dt);
  lights.update(viewport.camera, dt);
}

function frame(now: number) {
  if (!running || hiddenPump !== null) return;
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;

  fpsAccum += dt;
  frames++;
  if (fpsAccum >= 0.5) {
    fps = frames / fpsAccum;
    frames = 0;
    fpsAccum = 0;
  }

  step(dt);
  viewport.render(elapsed);
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

/**
 * A HIDDEN TAB GETS NO ANIMATION FRAMES AT ALL.
 *
 * Not throttled — suspended. On a solo round that is exactly right and the game
 * should pause. In a room it is not: the host owns everybody's world, so a host
 * who alt-tabs to answer a message freezes three other people mid-corridor, and
 * a client that stops sending commands looks to everyone else like it died.
 *
 * So when a networked page goes away, the loop moves onto a timer and keeps
 * simulating with nothing drawn. Chrome clamps background timers to one a second
 * unless the page is playing audio — ours always is, which is why this works in
 * practice — and the catch-up below covers the case where it doesn't, at the
 * cost of a slightly lumpy second for the people still watching.
 */
let hiddenPump: ReturnType<typeof setInterval> | null = null;

document.addEventListener('visibilitychange', () => {
  const away = document.hidden && net !== null;
  if (away === (hiddenPump !== null)) return;

  if (!away) {
    clearInterval(hiddenPump!);
    hiddenPump = null;
    last = performance.now();
    requestAnimationFrame(frame);
    return;
  }
  hiddenPump = setInterval(() => {
    if (!running) return;
    const now = performance.now();
    // Half a second of catch-up per pump. Enough to ride out a throttled timer;
    // short enough that returning from a ten-minute tab-out does not try to
    // simulate ten minutes of mall in one go.
    let owed = Math.min(0.5, (now - last) / 1000);
    last = now;
    while (owed > 0) {
      const dt = Math.min(1 / 30, owed);
      owed -= dt;
      step(dt);
    }
  }, 1000 / 60);
});

/**
 * Debug surface. The screenshot/QA harness drives the game exclusively through
 * this object, so it is a real API with a stable shape rather than a scratch pad.
 */
const debugApi = {
  viewport,
  mall,
  /**
   * The three.js namespace itself.
   *
   * A harness that has to answer a geometric question — does this frustum
   * enclose that bounding box — needs matrices and boxes, and reimplementing
   * them in the page from raw matrix elements is how you end up debugging the
   * harness instead of the game. Bundled either way; this only names it.
   */
  three: THREE,
  /**
   * Build any item's body on demand, detached from a round.
   *
   * The contact-sheet harness photographs all thirty-seven of them side by side
   * on a lit card, which is the only way to see that four of them are the same
   * silhouette in different colours.
   */
  lootBody,
  /** Every item id in the catalogue, for harnesses that sweep all of them. */
  lootIds: Object.keys(ITEMS),
  /**
   * Every harness entry point dismisses the lobby first.
   *
   * The overlay deliberately sits on top of a live mall rather than gating it,
   * which is what keeps the bare URL booting straight into a playable round —
   * but it is also a full-screen panel, so it would otherwise be baked into
   * every screenshot the art review takes.
   */
  setCamera(name: CameraName) {
    lobby.hide();
    setCamera(name);
  },
  cameras: Object.keys(MALL_CAMERAS) as CameraName[],
  /** Take control back after a camera shot. */
  play() {
    lobby.hide();
    setMode('play');
    return session?.snapshot ?? null;
  },
  get session() {
    return session;
  },
  /**
   * The hands, drivable directly.
   *
   * `tools/hands.ts` photographs poses that a scripted player cannot reliably
   * reach — halfway through prying a shutter, three frames into a shove — and
   * the honest way to hold one of those still is to feed the animation the state
   * it would have had and stop the clock, rather than to fish for the right
   * frame of a live round and screenshot whatever turns up.
   */
  get viewmodel() {
    return viewmodel;
  },
  /** How much of the walkable floor is dark enough to hide in. See the method. */
  exposureCensus(stride = 7) {
    return session?.exposureCensus(stride) ?? null;
  },
  /**
   * The whole playable state as plain data.
   *
   * QA asserts on this rather than on pixels: where the player is, what is in
   * the bag, what the crosshair is on and how far the hold has got. It is also
   * the thing to print when a bot script does something inexplicable.
   */
  get state() {
    return session?.snapshot ?? null;
  },

  /**
   * The whole networked state as plain data.
   *
   * Multiplayer is the one part of this game I cannot check by looking at it —
   * two browsers agreeing is not something a screenshot can show. So the duet
   * harness asserts on this: who is in the room, what this machine believes
   * about where the others are, and what it has cost in bytes.
   */
  get net() {
    if (!net) return null;
    const t = net.transport as WebSocketTransport;
    return {
      role: session?.role ?? null,
      self: t.self,
      isHost: t.isHost,
      open: t.open,
      tick: net.tick,
      lobbyUp: lobby.visible,
      roster: net.roster.map((r) => ({ ...r })),
      peers: [...t.peers],
      bytes: { ...t.stats },
      /** Where this machine thinks everybody is — the thing that must agree. */
      crew:
        session?.crew.all.map((a) => ({
          id: a.id,
          name: a.name,
          team: a.team,
          local: a.local,
          pos: [a.pos.x, a.pos.y, a.pos.z].map((v) => Number(v.toFixed(2))),
        })) ?? [],
    };
  },
  /** Drive the lobby without a mouse, for the same reason `bot` exists. */
  lobby: {
    visible: () => lobby.visible,
    team: (t: 'thief' | 'guard') => net?.setLobby({ team: t }),
    ready: (r: boolean) => net?.setLobby({ ready: r }),
    start: () => net?.start(),
  },

  /**
   * Voice, for the one QA leg that has to prove sound left one machine and
   * arrived at another.
   *
   * `tone` exists because headless Chrome will not give up a microphone —
   * `getUserMedia` hangs forever behind every fake-device flag there is — so
   * the harness synthesises one instead. That is not a compromise: the codec,
   * the gate, the wire, the jitter buffer and the panner are all identical, and
   * the only untested millimetre is the driver.
   */
  voice: {
    /** Open the real microphone. Prompts, and may well be refused. */
    async open() {
      return (await session?.openMic()) ?? false;
    },
    /** Open a synthesised one, loud enough to hold the gate open. */
    async tone(hz = 220) {
      const ctx = session?.audio?.engine.ctx;
      if (!ctx || !session) return false;
      // A suspended context's stream destination emits no frames at all, so the
      // encoder would sit there politely producing nothing.
      if (ctx.state !== 'running') await ctx.resume();
      const osc = ctx.createOscillator();
      osc.frequency.value = hz;
      const gain = ctx.createGain();
      gain.gain.value = 0.5;
      const dest = ctx.createMediaStreamDestination();
      osc.connect(gain).connect(dest);
      osc.start();
      return session.openMic(dest.stream);
    },
    mute(on: boolean) {
      if (session?.voice) session.voice.muted = on;
    },
    get stats() {
      return session?.voice?.stats ?? null;
    },
  },

  /** What the mix is doing, for the QA leg that cannot listen to it. */
  get audio() {
    const d = session?.audio;
    if (!d) return null;
    const e = d.engine as { stats?: { running: boolean; voices: number; named: number; bed: boolean; time: number } };
    return e.stats ?? null;
  },
  /** Script the player without a keyboard. See `Session.bot`. */
  get bot() {
    if (!session) throw new Error('session not ready');
    lobby.hide();
    setMode('play');
    return session.bot;
  },
  /**
   * Navigation, as plain data.
   *
   * A mall whose upper gallery is unreachable because one escalator landing is
   * twenty centimetres short still photographs perfectly — the guards simply
   * never go up there and nobody finds out. `upper` is the number of reachable
   * nodes above the ground floor, so this is the check.
   */
  nav(from?: [number, number, number]) {
    if (!session) return null;
    const nav = session.nav;
    const start = from
      ? { x: from[0], y: from[1], z: from[2] }
      : (mall.spawns.guards[0] ?? mall.spawns.thieves[0]);
    const t0 = performance.now();
    const reach = nav.reachable(start);
    const floodMs = Number((performance.now() - t0).toFixed(1));
    return {
      ...nav.stats,
      from: [start.x, start.y, start.z],
      reached: reach.nodes,
      upper: reach.upper,
      floodMs,
      // Anything here beyond the main space and the shuttered shops is a room
      // sealed off by accident, which is a class of bug no screenshot shows.
      islands: nav.islands(),
    };
  },
  /** Path between two points, with the cost of finding it. */
  navPath(from: [number, number, number], to: [number, number, number]) {
    if (!session) return null;
    const out: Array<{ x: number; y: number; z: number }> = [];
    const t0 = performance.now();
    const ok = session.nav.path(
      { x: from[0], y: from[1], z: from[2] },
      { x: to[0], y: to[1], z: to[2] },
      out,
    );
    const ms = Number((performance.now() - t0).toFixed(2));
    let len = 0;
    for (let i = 1; i < out.length; i++) {
      len += Math.hypot(out[i].x - out[i - 1].x, out[i].y - out[i - 1].y, out[i].z - out[i - 1].z);
    }
    return {
      ok,
      ms,
      expansions: session.nav.lastExpansions,
      points: out.length,
      metres: Number(len.toFixed(1)),
      path: out.map((p) => [Number(p.x.toFixed(1)), Number(p.y.toFixed(1)), Number(p.z.toFixed(1))]),
    };
  },
  /** Free look, for inspecting the map from anywhere the fixed cameras miss. */
  lookFrom(pos: [number, number, number], look: [number, number, number]) {
    lobby.hide();
    viewport.camera.position.set(...pos);
    viewport.camera.lookAt(new Vector3(...look));
    applyZoneFog();
    lights.snap(viewport.camera);
  },
  /** What the builder produced, so QA can assert on the level, not on pixels. */
  get levelStats() {
    return {
      colliders: mall.colliders.length,
      containers: mall.containers.length,
      barriers: mall.barriers.length,
      dropoffs: mall.dropoffs.length,
      cameras: mall.cameras.length,
      zones: mall.zones.length,
    };
  },
  /**
   * Every node in the scene whose name contains `needle`, with its world
   * position and whether it is actually being drawn.
   *
   * This exists because "I built the mesh but I cannot see it" is the single
   * most common failure in this codebase, and it has three completely different
   * causes — wrong transform, hidden behind something, or never added at all.
   * Guessing between them from a screenshot costs a full capture round each
   * time; asking the scene graph costs nothing.
   */
  probe(needle: string) {
    const out: Array<{ name: string; pos: [number, number, number]; visible: boolean }> = [];
    viewport.scene.traverse((o) => {
      if (!o.name.includes(needle)) return;
      const p = o.getWorldPosition(new Vector3());
      let visible = o.visible;
      for (let a = o.parent; a && visible; a = a.parent) visible = a.visible;
      out.push({ name: o.name, pos: [p.x, p.y, p.z], visible });
    });
    return out;
  },
  /**
   * Turn every node matching `needle` on or off, and say how many moved.
   *
   * The companion to `probe`: that one answers "is this being drawn", this one
   * takes it out of the frame so the cost of drawing it can be measured. An
   * A/B against a feature flag measures the flag, not the pixels — the guards
   * keep their geometry, their beams and their lights whether their brains are
   * thinking or not, and only hiding the roots actually removes them.
   */
  setVisible(needle: string, visible: boolean) {
    let n = 0;
    viewport.scene.traverse((o) => {
      if (!o.name.includes(needle)) return;
      o.visible = visible;
      n++;
    });
    return n;
  },
  /**
   * What the current camera actually hits at a normalised screen point, in
   * front-to-back order. The companion to `probe`: that one answers "is my mesh
   * in the scene", this one answers "and what is standing in front of it".
   */
  pick(x = 0, y = 0, limit = 6) {
    // Force the matrices current. `setCamera` only writes position/quaternion;
    // `matrixWorld` is refreshed by the renderer, so calling pick in the same
    // tick as setCamera raycasts from wherever the camera used to be — which
    // returns a perfectly plausible list of hits from the wrong place.
    viewport.camera.updateMatrixWorld(true);
    viewport.scene.updateMatrixWorld(true);
    const ray = new Raycaster();
    ray.setFromCamera(new Vector2(x, y), viewport.camera);
    return ray
      .intersectObject(viewport.scene, true)
      .slice(0, limit)
      .map((h) => {
        // Nearest NAMED ancestor, not `parent` — almost every mesh in the mall
        // hangs off an anonymous Group, so the immediate parent is a blank
        // string and the hit tells you nothing about what you are looking at.
        let owner = h.object.name;
        for (let a = h.object.parent; a && !owner; a = a.parent) owner = a.name;
        // Light pools and wall washes carry their label here instead of in a
        // name, so that they stay mergeable. They are also the single most
        // common thing to be picking AT — a floor gone milky is always a pool
        // that is too big — so the label has to survive somewhere. Merging
        // eventually eats it along with the mesh, which is what `?nobatch` is
        // for: chase a lighting fault there and read the answer here.
        const glow = h.object.userData.glow as string | undefined;
        const mat = (h.object as unknown as { material?: { type?: string; color?: { getHexString(): string } } })
          .material;
        return {
          owner: glow ?? owner ?? '(anonymous)',
          geo: (h.object as unknown as { geometry?: { type: string } }).geometry?.type ?? '',
          mat: `${mat?.type ?? '?'}#${mat?.color?.getHexString() ?? '?'}`,
          dist: Number(h.distance.toFixed(3)),
          at: h.point.toArray().map((v) => Number(v.toFixed(2))),
        };
      });
  },
  /**
   * Every mesh whose material expects vertex colours but whose geometry has
   * none. Each one renders pure black, no matter how well lit the room is.
   *
   * `surface()` sets `vertexColors: true` on all standard materials because that
   * is how the baked contact shading is carried. WebGL supplies (0,0,0) for a
   * missing attribute, so the material multiplies its albedo by zero and the
   * mesh disappears into a silhouette — which looks exactly like a lighting
   * problem and gets debugged as one. It cost a full round on the medieval suit
   * of armour, where the box parts were bright and the cylinder parts were black
   * in the same object.
   *
   * The fix is always the same: run the geometry through `flatVertexColor`. This
   * exists so the next one is found by asking rather than by staring.
   */
  auditVertexColors() {
    const bad = new Map<string, number>();
    viewport.scene.traverse((o) => {
      const m = o as { isMesh?: boolean; geometry?: { attributes?: Record<string, unknown> } };
      if (!m.isMesh || !m.geometry) return;
      const mats = (o as unknown as { material: unknown }).material;
      const list = Array.isArray(mats) ? mats : [mats];
      if (!list.some((x) => (x as { vertexColors?: boolean })?.vertexColors)) return;
      if (m.geometry.attributes?.color) return;
      let owner = o.name;
      for (let a = o.parent; a && !owner; a = a.parent) owner = a.name;
      const key = `${owner || '(unnamed)'} / ${(o as unknown as { geometry: { type: string } }).geometry.type}`;
      bad.set(key, (bad.get(key) ?? 0) + 1);
    });
    return [...bad].sort((a, b) => b[1] - a[1]).map(([where, count]) => ({ where, count }));
  },
  /**
   * Where the draw calls are, grouped by the nearest named ancestor.
   *
   * `renderInfo` says how many there are; this says whose they are. Forward
   * rendering makes every mesh its own call, so a building assembled from
   * thousands of small hand-authored boxes pays a CPU cost that has nothing to do
   * with triangle count — this map is the only way to tell which of those boxes
   * are worth merging and which are noise.
   *
   * `mats` counts DISTINCT material instances under that owner, because a bucket
   * with one material can be merged into one draw and a bucket with four hundred
   * cloned materials cannot be merged at all until the clones go away.
   */
  drawCensus(top = 20) {
    const byOwner = new Map<string, { meshes: number; mats: Set<unknown> }>();
    viewport.scene.traverse((o) => {
      if (!(o as { isMesh?: boolean }).isMesh || !o.visible) return;
      let owner = o.name;
      for (let a = o.parent; a && !owner; a = a.parent) owner = a.name;
      const key = owner || '(anonymous)';
      let row = byOwner.get(key);
      if (!row) byOwner.set(key, (row = { meshes: 0, mats: new Set() }));
      row.meshes++;
      row.mats.add((o as unknown as { material: unknown }).material);
    });
    return [...byOwner]
      .map(([owner, r]) => ({ owner, meshes: r.meshes, mats: r.mats.size }))
      .sort((a, b) => b.meshes - a.meshes)
      .slice(0, top);
  },
  /**
   * Real lights in the scene, by type, plus what the director is holding back.
   *
   * Worth having on tap because three.js forward-renders: every light in the
   * scene is evaluated by every lit fragment whether or not it is anywhere near
   * the camera. Measured on this map, forty-seven point lights ran at three
   * frames a second and eight ran at sixty — the cost is per-pixel, so it does
   * not care that the lamp is behind you.
   *
   * `PointLight` here should therefore always read exactly the pool size. If it
   * ever reads higher, something has created a light directly instead of
   * registering a source, and the frame rate has just fallen off a cliff.
   */
  get lightBudget() {
    const byType: Record<string, number> = {};
    viewport.scene.traverse((o) => {
      const l = o as unknown as { isLight?: boolean; type: string };
      if (l.isLight) byType[l.type] = (byType[l.type] ?? 0) + 1;
    });
    return { ...byType, pool: lights.stats, bake: mallBake() };
  },
  /** False until the lightmapper has finished. The harness waits on this. */
  get ready() {
    return ready;
  },
  /**
   * The freshly computed lightmap, base64'd, for `tools/bake.ts` to write to
   * disk. Null unless the page was loaded with `?rebake`.
   */
  get lightmap(): string | null {
    const buf = mallBakeEncoded();
    if (!buf) return null;
    const bytes = new Uint8Array(buf);
    // Chunked, because `String.fromCharCode(...bytes)` on a five megabyte array
    // is an argument list long enough to blow the stack.
    let s = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
      s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(s);
  },
  /**
   * Luminance statistics for the frame currently on screen.
   *
   * This exists because I cannot trust my own eyes on these captures. Reviewing
   * a PNG through a viewer that applies its own gamma made a mall where NOTHING
   * exceeded 240/255 look repeatedly blown out, and several rounds went into
   * dimming walls, ceiling strips and light pools that were never too bright —
   * while the genuinely broken rooms were the ones sitting at 55% pure black.
   *
   * So the harness prints these next to every shot. The two numbers that matter
   * are `p99`, which says whether the frame has any highlight at all, and
   * `black`, which says how much of it has fallen off the bottom. A moonlit
   * interior wants a low median with a real highlight on top of it; an image
   * with p99 near 110 and no hot pixels is not moody, it is underexposed.
   */
  histogram(samples = 480) {
    // Re-render immediately before reading. Without `preserveDrawingBuffer` the
    // WebGL back buffer is undefined once the compositor has swapped, and
    // `drawImage` quietly yields a blank canvas rather than failing.
    viewport.render(running ? elapsed : 1.0);
    const src = viewport.renderer.domElement;
    const h = Math.max(1, Math.round((samples * src.height) / src.width));
    const c = document.createElement('canvas');
    c.width = samples;
    c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true })!;
    ctx.drawImage(src, 0, 0, samples, h);
    const data = ctx.getImageData(0, 0, samples, h).data;

    const counts = new Uint32Array(256);
    let sum = 0;
    const n = samples * h;
    for (let i = 0; i < data.length; i += 4) {
      const l = Math.round(0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]);
      counts[l]++;
      sum += l;
    }
    const at = (q: number) => {
      let acc = 0;
      const want = q * n;
      for (let v = 0; v < 256; v++) {
        acc += counts[v];
        if (acc >= want) return v;
      }
      return 255;
    };
    let hot = 0;
    for (let v = 241; v < 256; v++) hot += counts[v];
    let black = 0;
    for (let v = 0; v < 12; v++) black += counts[v];
    return {
      mean: Math.round(sum / n),
      p50: at(0.5),
      p90: at(0.9),
      p99: at(0.99),
      hot: Number(((hot / n) * 100).toFixed(2)),
      black: Number(((black / n) * 100).toFixed(1)),
    };
  },
  get fps() {
    return Math.round(fps);
  },
  /**
   * Median CPU cost of one rendered frame, in milliseconds.
   *
   * `fps` is not a usable optimisation signal here. It is sampled from the
   * animation loop, and the animation loop is paced by the display — in the
   * headless harness that is a fixed cadence that does not move no matter what
   * the frame actually costs, which is why a four-fold cut in draw calls left it
   * reading exactly fifteen both before and after. This renders back to back
   * with no frame pacing in the way, so the number is the work itself.
   *
   * Median rather than mean: the first frames after a camera move pay for
   * shader compilation and texture upload, and one 400ms outlier drags an
   * average far enough to hide a real win.
   */
  frameProbe(samples = 60) {
    const t: number[] = [];
    for (let i = 0; i < samples; i++) {
      const t0 = performance.now();
      viewport.render(1.0 + i * 0.016);
      t.push(performance.now() - t0);
    }
    t.sort((a, b) => a - b);
    const pick = (q: number) => Number(t[Math.min(t.length - 1, Math.floor(q * t.length))].toFixed(2));
    return { ms: pick(0.5), best: pick(0), p90: pick(0.9), impliedFps: Math.round(1000 / pick(0.5)) };
  },
  get renderInfo() {
    const info = viewport.renderer.info;
    return {
      calls: info.render.calls,
      triangles: info.render.triangles,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
      programs: info.programs?.length ?? 0,
    };
  },
  /** Pause the loop so a screenshot is deterministic (grain is time-driven). */
  freeze(at = 1.0) {
    running = false;
    // The harness screenshots the whole page, not the canvas, so the overlays
    // would otherwise end up baked into every captured frame.
    lobby.hide();
    viewer.setChromeVisible(false);
    viewport.render(at);
  },
  /**
   * Step the world by a whole number of fixed ticks and draw nothing. The only
   * way the world moves under `?capture`, and the reason two capture runs of one
   * commit agree to the byte.
   */
  advance(seconds: number) {
    const ticks = Math.round(seconds * 60);
    for (let i = 0; i < ticks; i++) step(1 / 60);
    return { ticks, elapsed: Number(elapsed.toFixed(3)) };
  },
  resume() {
    viewer.setChromeVisible(true);
    // Under `?capture` the loop stays dead: real time must never reach the sim.
    if (running || CAPTURE) return;
    running = true;
    last = performance.now();
    requestAnimationFrame(frame);
  },
  setGrade(patch: Record<string, number>) {
    for (const [k, v] of Object.entries(patch)) {
      const u = viewport.grade.uniforms[k as keyof typeof viewport.grade.uniforms];
      if (u) u.value = v;
    }
  },
};

(window as unknown as { __game: typeof debugApi }).__game = debugApi;
console.info(
  '[mall] booted.',
  JSON.stringify(debugApi.levelStats),
  'cameras:',
  debugApi.cameras.join(', '),
);
